import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Typography,
  useTheme,
  Skeleton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { AreaChart, Line, ResponsiveContainer, Area, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '../../../../assets/icons/Expand.svg';
import TableChartIcon from '../../../../assets/icons/Eye.svg';
import EditIcon from '../../../../assets/icons/edit.svg';
import RestartAltIcon from '../../../../assets/icons/Reset.svg';
import upIcon from 'packages/novous_web/src/assets/icons/up_icon.svg';
import downIcon from 'packages/novous_web/src/assets/icons/down_icon.svg';
import EditOverspeedingModal from './EditDataModal';
import EditDataModal from './EditDataModal';
import CollapseIcon from '../../../../assets/icons/CollapseIcon.svg';
import CommonAnalysisTable from './CommonAnalysisTable';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import NoDataIcon from 'packages/novous_web/src/assets/icons/NoData.svg';

// Custom tooltip for chart hover
interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: any;
  unit?: string;
}

// Format date to show date in DD/MM/YYYY format (e.g., "12/08/2025")
const formatTooltipDate = (dateString: string | number) => {
  try {
    const dateStr = String(dateString);
    // If already in DD/MM/YYYY format, return as is
    if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateStr;
    }
    return dateStr;
  } catch {
    return String(dateString);
  }
};

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label, unit }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const roundedValue = Math.round(data.y * 100) / 100; // Round to 2 decimal places
    return (
      <div
        style={{
          position: 'relative',
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: 14,
          color: '#232323',
          fontWeight: 500,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Show date above */}
        <div
          style={{
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '12px',
            lineHeight: '18px',
            letterSpacing: '0%',
            color: '#767676',
            marginBottom: 2,
          }}
        >
          {formatTooltipDate(data.x)}
        </div>
        {/* Show value/unit below */}
        <div style={{ fontWeight: 700 }}>
          {roundedValue}
          {unit && (
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0%',
                color: '#080808',
                marginLeft: '4px',
              }}
            >
              {unit}
            </Typography>
          )}
        </div>
        {/* Triangle pointer at bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: '10px solid #fff',
          }}
        />
      </div>
    );
  }
  return null;
};

// Format date to show date and month (e.g., "15 Jan")
const formatXAxisDate = (dateString: string) => {
  try {
    // Handle different date formats (DD/MM/YYYY or other formats)
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[parseInt(month) - 1]}`;
    }
    return dateString;
  } catch {
    return dateString;
  }
};

// Format date to show date, month and year (e.g., "15 Jan 2024")
const formatFullDate = (dateString: string | number) => {
  try {
    const dateStr = String(dateString);
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[parseInt(month) - 1]} ${year}`;
    }
    return dateStr;
  } catch {
    return String(dateString);
  }
};

interface AnalyticCardProps {
  title: string;
  value: string | number;
  unit?: string;
  icon: React.ReactNode;
  infoIcon?: React.ReactNode;
  menuIcon?: React.ReactNode;
  trend?: string;
  trendValue?: string;
  trendColor?: string;
  chartData: Array<{ x: string | number; y: number }>;
  chartId?: number;
  onDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;
  tooltipMessage?: string;
  tableTitle?: string;
  tableColumns?: Array<{ key: string; label: string; align?: 'left' | 'center' | 'right' }>;
  tableData?: any[];
  tableId?: number;
  onTableClick?: () => void;
  paginationInfo?: any;
  onTablePaginationChange?: (cursor?: any) => void;
}

const AnalyticCard: React.FC<AnalyticCardProps> = ({
  title,
  value,
  unit,
  icon,
  infoIcon,
  menuIcon,
  trend,
  trendValue,
  trendColor = '#2FCB89',
  chartData,
  chartId,
  onDataUpdate,
  tooltipMessage,
  tableTitle,
  tableColumns,
  tableData,
  tableId,
  onTableClick,
  paginationInfo,
  onTablePaginationChange,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  const open = Boolean(anchorEl);

  // Check if data is loading (empty or null chartData)
  const isLoading = !chartData || chartData.length === 0;

  // Calculate total from chart data or use passed value
  const totalValue = isLoading ? 0 : chartData.reduce((sum, item) => sum + item.y, 0);
  // Use passed value prop if loading, otherwise calculate from chart data
  const formattedTotal = isLoading
    ? value // Use the passed value prop (which will be 'N/A' when no data)
    : title === 'Fleet Distance Travelled'
    ? totalValue.toFixed(2)
    : (totalValue / chartData.length).toFixed(1).toString();

  // Calculate percentage change between latest and previous date
  const calculateTrendData = () => {
    if (isLoading || chartData.length < 2) {
      return { trendDirection: 'up', percentageChange: '0.0%' };
    }

    const latestValue = chartData[chartData.length - 1].y;
    const previousValue = chartData[chartData.length - 2].y;

    if (previousValue === 0) {
      return {
        trendDirection: latestValue > 0 ? 'up' : 'down',
        percentageChange: '0.0%',
      };
    }

    const change = ((latestValue - previousValue) / previousValue) * 100;
    const trendDirection = change >= 0 ? 'up' : 'down';
    const percentageChange = `${Math.abs(change).toFixed(1)}%`;

    return { trendDirection, percentageChange };
  };

  const { trendDirection, percentageChange } = calculateTrendData();

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    console.log('Expand clicked for:', title);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTable = () => {
    console.log('Table clicked for:', title);
    // Call the onTableClick callback to fetch data if provided
    if (onTableClick) {
      onTableClick();
    }
    setIsTableModalOpen(true);
    handleMenuClose();
  };

  const handleCloseTableModal = () => {
    setIsTableModalOpen(false);
  };

  const handleEditData = () => {
    console.log('Edit Data clicked for:', title);
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleApplyEditData = (data: any) => {
    console.log('Applied edit data:', data);
    // Call the onDataUpdate callback to refresh the specific chart data
    if (onDataUpdate && chartId) {
      onDataUpdate(chartId, data.startDate, data.endDate);
      // Show toast message
      const startDateFormatted = new Date(data.startDate).toLocaleDateString('en-GB');
      const endDateFormatted = new Date(data.endDate).toLocaleDateString('en-GB');
      showToast(`Date range applied: ${startDateFormatted} to ${endDateFormatted}`, 'success');
    } else {
      console.log('Cannot update: onDataUpdate or chartId missing');
    }
  };

  const handleResetChanges = () => {
    console.log('Reset Changes clicked for:', title);
    handleMenuClose();
    setIsEditModalOpen(false);
    setResetTrigger((prev) => prev + 1);
    // Reset to last 7 days
    if (onDataUpdate && chartId) {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onDataUpdate(chartId, startDate, endDate);
    }
  };

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.04)',
        paddingTop: 2,
        minWidth: 260,
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0px 4px 32px 0px rgba(0, 0, 0, 0.08)',
          border: '0.5px solid #D4D1F6',
          borderRadius: '8px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          {icon}
          <Typography
            sx={{
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '0%',
              verticalAlign: 'middle',
              color: '#333333',
              textAlign: 'left',
              flex: 1,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 'fit-content' }}>
          {infoIcon && (
            <MuiTooltip title={tooltipMessage || 'Information'} arrow>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 14,
                  height: 14,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                {infoIcon}
              </Box>
            </MuiTooltip>
          )}
          {menuIcon && (
            <IconButton
              size="small"
              sx={{
                p: 0,
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
              onClick={handleMenuClick}
            >
              {menuIcon}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '160px',
          },
        }}
      >
        <MenuItem onClick={handleExpand} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={OpenInFullIcon} alt="Expand" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Expand</Typography>
        </MenuItem>
        <MenuItem onClick={handleTable} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={TableChartIcon} alt="Table" style={{ width: 24, height: 24 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Table</Typography>
        </MenuItem>
        <MenuItem onClick={handleEditData} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
          <Typography sx={{ fontSize: '14px', color: '#333' }}>Edit Data</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleResetChanges}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
        >
          <img
            src={RestartAltIcon}
            alt="Reset"
            style={{
              width: 20,
              height: 20,
              filter:
                'invert(34%) sepia(68%) saturate(2449%) hue-rotate(338deg) brightness(91%) contrast(84%)',
            }}
          />
          <Typography sx={{ fontSize: '14px', color: '#DC3545' }}>Reset Changes</Typography>
        </MenuItem>
      </Menu>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mt: 1,
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            pl: '40px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoading ? (
              <Typography
                sx={{
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '24px',
                  lineHeight: '36px',
                  letterSpacing: '0%',
                  verticalAlign: 'bottom',
                  color: '#333333',
                }}
              >
                {value}
              </Typography>
            ) : (
              <>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '24px',
                    lineHeight: '36px',
                    letterSpacing: '0%',
                    verticalAlign: 'bottom',
                    color: '#333333',
                  }}
                >
                  {formattedTotal}
                </Typography>
                {unit && (
                  <>
                    {unit.startsWith('/') ? (
                      <>
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 600,
                            fontStyle: 'normal',
                            fontSize: '24px',
                            lineHeight: '36px',
                            letterSpacing: '0%',
                            verticalAlign: 'bottom',
                            color: '#808080',
                          }}
                        >
                          /
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 600,
                            fontStyle: 'normal',
                            fontSize: '18px',
                            lineHeight: '27px',
                            letterSpacing: '0%',
                            verticalAlign: 'bottom',
                            color: '#808080',
                          }}
                        >
                          {unit.substring(1)}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontStyle: 'normal',
                          fontSize: '18px',
                          lineHeight: '27px',
                          letterSpacing: '0%',
                          verticalAlign: 'bottom',
                          color: '#808080',
                        }}
                      >
                        {unit}
                      </Typography>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '65px',
                height: '26px',
                gap: '4px',
                paddingTop: '4px',
                paddingRight: '8px',
                paddingBottom: '4px',
                paddingLeft: '8px',
                borderRadius: '8px',
                opacity: 1,
                background: '#F0F0F0',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  verticalAlign: 'bottom',
                  color: '#888',
                }}
              >
                --
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '65px',
                height: '26px',
                gap: '4px',
                paddingTop: '4px',
                paddingRight: '8px',
                paddingBottom: '4px',
                paddingLeft: '8px',
                borderRadius: '8px',
                opacity: 1,
                background: trendDirection === 'up' ? '#D9EDE6' : '#F7E1E1',
              }}
            >
              <img
                src={trendDirection === 'up' ? upIcon : downIcon}
                alt={trendDirection === 'up' ? 'up' : 'down'}
                style={{ width: 12, height: 12 }}
              />
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '0%',
                  verticalAlign: 'bottom',
                  color: trendDirection === 'up' ? '#00875A' : '#C83333',
                }}
              >
                {percentageChange}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ width: '100%', height: 100, mt: 2, position: 'relative' }}>
        {isLoading ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(85, 71, 221, 0.02)',
              borderRadius: '8px',
            }}
          >
            <img src={NoDataIcon} alt="No data" className="w-32 h-32 opacity-50" />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cardAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5547DD" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#5547DD" stopOpacity={0} />
                </linearGradient>
              </defs>
              {/* Stronger fill below the line for all graphs */}
              <Area
                type="monotone"
                dataKey="y"
                stroke={theme.palette.primary.main}
                strokeWidth={1}
                fill="url(#cardAreaGradient)"
                fillOpacity={0.9}
              />
              <Line type="monotone" dataKey="y" stroke="#5547DD" strokeWidth={1} dot={true} />
              {/* Tooltip on hover */}
              <Tooltip
                content={(props) => <ChartTooltip {...props} unit={unit} />}
                cursor={{ stroke: '#5547DD', strokeWidth: 2, strokeDasharray: '3 3' }}
                position={{ y: -50 }}
                offset={30}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* Expanded Chart Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="expanded-chart-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '70%',
            maxWidth: '1400px',
            height: '85vh',
            maxHeight: '85vh',
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: 24,
            p: { xs: 2, sm: 2.5, md: 3 },
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: { xs: 1.5, sm: 2 },
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '6px', sm: '10px' } }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  gap: '10px',
                  borderRadius: '8px',
                  padding: '4px',
                  background: '#EAE9F5',
                  opacity: 1,
                  transform: 'rotate(0deg)',
                }}
              >
                {icon}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: { xs: '14px', sm: '16px', md: '18px' },
                    lineHeight: { xs: '20px', sm: '24px', md: '27px' },
                    letterSpacing: '0%',
                    color: '#1B1B1B',
                  }}
                >
                  {title}
                </Typography>
                {infoIcon && (
                  <MuiTooltip title={tooltipMessage || 'Information'} arrow placement="right">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        cursor: 'pointer',
                      }}
                    >
                      {infoIcon}
                    </Box>
                  </MuiTooltip>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                onClick={handleEditData}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  colour: '#5547DD',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={EditIcon} alt="Edit" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Edit Chart
                </Typography>
              </Box>
              <Box
                onClick={handleCloseModal}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(85, 71, 221, 0.1)',
                  },
                }}
              >
                <img src={CollapseIcon} alt="Collapse" style={{ width: 16, height: 16 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: '0%',
                    color: '#5547DD',
                  }}
                >
                  Collapse
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Value Display */}
          <Box sx={{ mb: { xs: 1.5, sm: 2 }, ml: { xs: 1, sm: 2, md: 5 }, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              {isLoading ? (
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: { xs: '36px', sm: '48px', md: '60px', lg: '72px' },
                    lineHeight: { xs: '48px', sm: '64px', md: '78px', lg: '94px' },
                    letterSpacing: '0%',
                    verticalAlign: 'bottom',
                    color: '#333333',
                  }}
                >
                  {value}
                </Typography>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: { xs: '36px', sm: '48px', md: '60px', lg: '72px' },
                      lineHeight: { xs: '48px', sm: '64px', md: '78px', lg: '94px' },
                      letterSpacing: '0%',
                      verticalAlign: 'bottom',
                      color: '#333333',
                    }}
                  >
                    {formattedTotal}
                  </Typography>
                  {unit && (
                    <>
                      {unit.startsWith('/') ? (
                        <>
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 400,
                              fontSize: { xs: '36px', sm: '48px', md: '60px', lg: '72px' },
                              lineHeight: { xs: '48px', sm: '64px', md: '78px', lg: '94px' },
                              letterSpacing: '0%',
                              verticalAlign: 'bottom',
                              color: '#888',
                            }}
                          >
                            /
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 400,
                              fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '24px' },
                              lineHeight: { xs: '22px', sm: '26px', md: '28px', lg: '31px' },
                              letterSpacing: '0%',
                              verticalAlign: 'bottom',
                              color: '#888',
                            }}
                          >
                            {unit.substring(1)}
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '24px' },
                            lineHeight: { xs: '22px', sm: '26px', md: '28px', lg: '31px' },
                            letterSpacing: '0%',
                            verticalAlign: 'bottom',
                            color: '#888',
                          }}
                        >
                          {unit}
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Expanded Chart */}
          <Box
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              mt: { xs: 2, sm: 2.5, md: 3 },
              position: 'relative',
            }}
          >
            {!isLoading && (
              <Typography
                sx={{
                  position: 'absolute',
                  left: { xs: '10px', sm: '15px', md: '20px' },
                  top: '70%',
                  transform: 'rotate(-90deg) translateY(-50%)',
                  transformOrigin: 'left center',
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '14px', md: '16px', lg: '24px' },
                  //lineHeight: { xs: '18px', sm: '20px', md: '24px', lg: '40px' },
                  letterSpacing: '0%',
                  textAlign: 'center',
                  color: '#1B1B1B',
                  whiteSpace: 'nowrap',
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              >
                {title === 'Fleet Distance Travelled' ? `Km of ${title}` : `No. of ${title}`}
              </Typography>
            )}
            {isLoading ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(85, 71, 221, 0.02)',
                  borderRadius: '8px',
                }}
              >
                <img src={NoDataIcon} alt="No data" className="w-48 h-48 opacity-50" />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <linearGradient id="modalAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#5547DD" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#5547DD" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
                  <XAxis
                    dataKey="x"
                    tick={{ fill: '#888', fontSize: 14 }}
                    axisLine={{ stroke: '#E5E5E5' }}
                    tickLine={false}
                    tickFormatter={formatXAxisDate}
                    interval="preserveStartEnd"
                    padding={{ left: 40, right: 40 }}
                  />
                  <YAxis tick={{ fill: '#888', fontSize: 14 }} axisLine={false} tickLine={false} width={80} />
                  <Area
                    type="monotone"
                    dataKey="y"
                    stroke="#5547DD"
                    strokeWidth={2.5}
                    fill="url(#modalAreaGradient)"
                    fillOpacity={1}
                    dot={false}
                  />
                  <Tooltip
                    content={(props) => <ChartTooltip {...props} unit={unit} />}
                    cursor={{ stroke: '#5547DD', strokeWidth: 1, strokeDasharray: '5 5' }}
                    position={{ y: -50 }}
                    offset={30}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Date Range Footer */}
          <Box sx={{ textAlign: 'center', mt: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
            <Typography
              sx={{
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: { xs: '12px', sm: '14px', md: '18px', lg: '24px' },
                //lineHeight: { xs: '18px', sm: '20px', md: '24px', lg: '40px' },
                letterSpacing: '0%',
                textAlign: 'center',
                color: '#1B1B1B',
              }}
            >
              {chartData.length > 0 &&
                `${formatFullDate(chartData[0]?.x)} - ${formatFullDate(chartData[chartData.length - 1]?.x)}`}
            </Typography>
          </Box>
        </Box>
      </Modal>

      {/* Edit Overspeeding Modal */}
      <EditDataModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onApply={handleApplyEditData}
        onReset={() => console.log('Reset clicked')}
        cardTitle={title}
        chartId={chartId}
        resetTrigger={resetTrigger}
      />

      {/* Table View Modal */}
      <Modal open={isTableModalOpen} onClose={handleCloseTableModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            height: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {tableData && tableColumns && (
            <CommonAnalysisTable
              title={tableTitle || `Table View – ${title}`}
              headerText={`Detailed breakdown of ${title.toLowerCase()}`}
              data={tableData}
              columns={tableColumns}
              onClose={handleCloseTableModal}
              paginationInfo={paginationInfo}
              onPaginationChange={onTablePaginationChange}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default AnalyticCard;
